// Services & tools view — the service-catalog + activation model.
//
// Admin view:   sees ALL service definitions, including SHARED templates (active
//   + inactive). Can create (shared|personal), edit, and globally activate /
//   deactivate a shared template (PATCH active — a toggle). Publications
//   management (publish/approve/suspend) is kept as-is.
// User view:    sees the CATALOG = shared ACTIVE services + their own personal
//   services. Per shared service an "Enable for me" toggle (POST activation,
//   reflecting active_for_me). May add a personal service and edit their own.
//   The admin-only global toggle and inactive shared defs are never shown.
//
// Affordances switch on `isAdmin` from auth. Every response is Zod-validated.

import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useLiveReseed, publicationsSig } from '../lib/useLiveReseed';
import { useAuth } from '../auth/AuthContext';
import { Card, Loading, ErrorState, Badge, JsonView } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Modal, ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import { friendly } from './Networks';
import { TestPanelDrawer } from './TestPanel';
import { imageVersion, serviceCategory, relativeTime } from '../lib/serviceMeta';
import type {
  ServiceCatalogEntry,
  CatalogSource,
  DockerHubSearchResult,
  ServiceKind,
  ServiceToolSummary,
  ServicePublication,
  PublishNetworkState,
} from '../api/schemas';

// Source badge tone: built-in config/db vs external remote/docker.
function sourceTone(kind: string): 'info' | 'muted' | 'ok' {
  if (kind === 'config') return 'muted';
  if (kind === 'db') return 'info';
  return 'ok';
}

// Client-side mirror of the server catalog filter (H14): free-text over
// name/description/tags/tool names, plus tag/source/type/status filters (AND).
export function matchesFilters(
  e: ServiceCatalogEntry,
  f: { q: string; tags: string[]; source: string; type: string; status: string },
): boolean {
  const q = f.q.trim().toLowerCase();
  if (q) {
    const hay = [e.name, e.description ?? '', ...e.tags, ...e.tools.map((t) => t.name)]
      .join(' ')
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.tags.length) {
    const et = new Set(e.tags.map((t) => t.toLowerCase()));
    if (!f.tags.every((t) => et.has(t.toLowerCase()))) return false;
  }
  if (f.source && e.source !== f.source && e.source_kind !== f.source) return false;
  if (f.type && e.kind !== f.type) return false;
  if (f.status === 'active' && !e.active_for_me) return false;
  if (f.status === 'inactive' && e.active_for_me) return false;
  return true;
}

// H25: the networks a service is currently published into, derived from the H15
// publication data (one entry per service_definition × network). Used by the
// PUBLISHED IN column (replacing the old Publications block).
export function publishedNets(serviceDefinitionId: string, pubs: ServicePublication[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of pubs) {
    if (p.service_definition_id !== serviceDefinitionId) continue;
    const label = p.network_slug ?? p.network_id;
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

export function Services() {
  const { isAdmin, user } = useAuth();
  const catalog = useResource(() => api.serviceCatalog(), []);
  const pubs = useResource(() => api.publications(), []);
  // Live, not polled: the "Published in" column reconciles when a publication.changed frame arrives
  // (publish/approve/suspend/withdraw) and on bus (re)connect. The catalog itself has no bus event, so it
  // reloads on the user's own create/edit/activation actions (handled inline below).
  useLiveReseed(pubs.reload, publicationsSig);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [openEntry, setOpenEntry] = useState<ServiceCatalogEntry | null>(null);
  // H15: the network slide-out panel. `auto` marks it as auto-opened right after an activation so the
  // panel can prompt the user to choose networks.
  const [publishPanel, setPublishPanel] = useState<{ entry: ServiceCatalogEntry; auto: boolean } | null>(null);

  // H14 search/filter state.
  const [query, setQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const allEntries = catalog.data?.items ?? [];
  const pubItems = pubs.data?.items ?? [];
  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of allEntries) for (const t of e.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
  }, [allEntries]);
  const allSources = useMemo(() => {
    const s = new Map<string, string>();
    for (const e of allEntries) s.set(e.source, e.source_kind);
    return [...s.entries()];
  }, [allEntries]);
  const filterState = { q: query, tags: selectedTags, source: sourceFilter, type: typeFilter, status: statusFilter };
  const filteredEntries = useMemo(
    // Pre-sort alphabetically by name so the table's stable default sort by Category yields
    // category-grouped rows that stay name-ordered WITHIN each category.
    () =>
      allEntries
        .filter((e) => matchesFilters(e, filterState))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allEntries, query, selectedTags, sourceFilter, typeFilter, statusFilter],
  );
  const toggleTag = (t: string) =>
    setSelectedTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const filtersActive = !!(query || selectedTags.length || sourceFilter || typeFilter || statusFilter);

  const myId = user?.id;
  const canManageEntry = (e: ServiceCatalogEntry) =>
    isAdmin || (e.kind === 'personal' && e.owner_id === myId);

  // H40 — the toggle (Active) is the FIRST column; every column header is click-to-sort.
  const catCols: Column<ServiceCatalogEntry>[] = [
    {
      key: 'active',
      header: isAdmin ? 'Active (global)' : 'Enabled for me',
      align: 'center',
      sortValue: (e) => (e.kind === 'personal' ? true : isAdmin ? e.active : e.active_for_me),
      cell: (e) => (
        <ActiveToggle
          entry={e}
          isAdmin={isAdmin}
          onChanged={catalog.reload}
          onActivated={() => setPublishPanel({ entry: e, auto: true })}
        />
      ),
    },
    {
      key: 'name',
      header: 'Service',
      sortValue: (e) => e.name,
      cell: (e) => (
        <div>
          <b>{e.name}</b>
          {e.description && <div className="muted small svc-desc">{e.description}</div>}
          {e.tags.length > 0 && (
            <div className="svc-tags">
              {e.tags.slice(0, 4).map((t) => (
                <span key={t} className="chip chip-tag">{t}</span>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortValue: (e) => serviceCategory(e.tags),
      cell: (e) => <span className="chip chip-tag">{serviceCategory(e.tags)}</span>,
    },
    {
      key: 'kind',
      header: 'Catalog',
      sortValue: (e) => e.kind,
      cell: (e) =>
        e.kind === 'shared' ? (
          <Badge tone="info">shared</Badge>
        ) : (
          <Badge tone="muted">personal</Badge>
        ),
    },
    {
      key: 'source',
      header: 'Source',
      sortValue: (e) => e.source,
      cell: (e) => <Badge tone={sourceTone(e.source_kind)}>{e.source}</Badge>,
    },
    {
      key: 'version',
      header: 'Version',
      sortValue: (e) => imageVersion(e.image_ref),
      cell: (e) => <code className="mono small" title={e.image_ref}>{imageVersion(e.image_ref)}</code>,
    },
    { key: 'image', header: 'Image', sortValue: (e) => e.image_ref, cell: (e) => <code className="mono small">{e.image_ref}</code> },
    { key: 'tools', header: 'Tools', align: 'right', sortValue: (e) => e.tools.length, cell: (e) => e.tools.length },
    {
      key: 'updated',
      header: 'Updated',
      sortValue: (e) => e.updated_at ?? e.created_at ?? '',
      cell: (e) => {
        const ts = e.updated_at ?? e.created_at;
        return ts ? <span className="muted small" title={String(ts)}>{relativeTime(ts)}</span> : <span className="muted">—</span>;
      },
    },
    {
      key: 'gpu',
      header: 'GPU',
      sortValue: (e) => e.gpu_class?.min_vram_gb ?? null,
      cell: (e) =>
        e.gpu_class?.min_vram_gb != null ? <span className="chip">≥{e.gpu_class.min_vram_gb}GB</span> : '—',
    },
    {
      key: 'networks',
      header: 'Published in',
      sortValue: (e) => publishedNets(e.id, pubItems).length,
      cell: (e) => {
        const nets = publishedNets(e.id, pubItems);
        return (
          <div className="svc-pubnets">
            {nets.length > 0 ? (
              <span className="svc-tags">
                {nets.map((n) => <span key={n} className="chip">{n}</span>)}
              </span>
            ) : (
              <span className="muted small">{e.active_for_me ? 'not published' : '—'}</span>
            )}
            {e.active_for_me && (
              <button
                className="btn btn-sm"
                title="Choose the networks this service is available in"
                onClick={(ev) => {
                  ev.stopPropagation();
                  setPublishPanel({ entry: e, auto: false });
                }}
              >
                Manage
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="view">
      <div className="view-head">
        <h1>Services</h1>
        <div className="view-head-actions">
          <button className="btn" onClick={() => setCreating(true)}>
            {isAdmin ? '+ New service' : '+ Add personal service'}
          </button>
          <button className="btn" onClick={() => setImporting(true)}>🐳 Import from Docker Hub</button>
          {/* H39 — the "Publish service" button is gone. Publishing is now done by checking the
              target network in a service's slide-out settings (H15 NetworkPublishDrawer). */}
        </div>
      </div>

      {isAdmin && <CatalogSourcesCard onChanged={catalog.reload} />}

      <Card title={isAdmin ? 'Service catalog (shared templates + personal)' : 'Service catalog (templates)'}>
        {catalog.loading ? (
          <Loading />
        ) : catalog.error || !catalog.data ? (
          <ErrorState message={catalog.error ?? 'no data'} onRetry={catalog.reload} />
        ) : (
          <>
            <div className="catalog-toolbar">
              <input
                className="catalog-search"
                type="search"
                placeholder="Search name, description, tags, tools…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="">All sources</option>
                {allSources.map(([id]) => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                <option value="shared">shared</option>
                <option value="personal">personal</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Any status</option>
                <option value="active">active for me</option>
                <option value="inactive">inactive</option>
              </select>
              {filtersActive && (
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    setQuery('');
                    setSelectedTags([]);
                    setSourceFilter('');
                    setTypeFilter('');
                    setStatusFilter('');
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {allTags.length > 0 && (
              <div className="catalog-tagbar">
                {allTags.map((t) => (
                  <button
                    key={t}
                    className={`chip chip-tag${selectedTags.includes(t) ? ' chip-tag-on' : ''}`}
                    onClick={() => toggleTag(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            <DataTable
              columns={catCols}
              rows={filteredEntries}
              rowKey={(e) => e.id}
              empty={filtersActive ? 'No services match your filters.' : 'No services in your catalog yet.'}
              onRowClick={(e) => setOpenEntry(e)}
              initialSort={{ key: 'category', dir: 'asc' }}
            />
            <p className="muted small">
              Showing {filteredEntries.length} of {allEntries.length} services.{' '}
              {isAdmin
                ? 'Shared templates are visible to everyone in the org once activated. The Active toggle activates / deactivates a shared template globally.'
                : 'Toggle a shared service to enable or disable it for yourself. Personal services are always active and yours alone.'}
            </p>
          </>
        )}
      </Card>

      {creating && (
        <ServiceDefinitionModal
          isAdmin={isAdmin}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            catalog.reload();
          }}
        />
      )}

      {importing && (
        <DockerHubImportModal
          isAdmin={isAdmin}
          onClose={() => setImporting(false)}
          onImported={() => {
            setImporting(false);
            catalog.reload();
          }}
        />
      )}

      {openEntry && (
        <ServiceEntryDrawer
          entry={openEntry}
          canManage={canManageEntry(openEntry)}
          isAdmin={isAdmin}
          onClose={() => setOpenEntry(null)}
          onChanged={(next) => {
            catalog.reload();
            setOpenEntry(next);
          }}
          onManageNetworks={() => setPublishPanel({ entry: openEntry, auto: false })}
        />
      )}

      {publishPanel && (
        <NetworkPublishDrawer
          entry={publishPanel.entry}
          autoOpened={publishPanel.auto}
          onClose={() => setPublishPanel(null)}
          onChanged={() => {
            pubs.reload();
          }}
        />
      )}
    </div>
  );
}

// H15 slide-out: choose the networks a service is published (= available) in. Toggling a network
// applies live (PUT publish-networks → create-or-approve / withdraw the publication). Auto-opens right
// after an activation so the user immediately picks where the service is available.
function NetworkPublishDrawer({
  entry,
  autoOpened,
  onClose,
  onChanged,
}: {
  entry: ServiceCatalogEntry;
  autoOpened: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const panel = useResource(() => api.publishNetworks(entry.id), [entry.id]);
  const [busyNet, setBusyNet] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const nets = panel.data?.networks ?? [];
  const publishedCount = nets.filter((n) => n.published).length;
  const unsupported = !!(panel.error && /404|not found/i.test(panel.error));

  async function toggle(net: PublishNetworkState) {
    const selected = new Set(nets.filter((n) => n.published).map((n) => n.network_id));
    if (selected.has(net.network_id)) selected.delete(net.network_id);
    else selected.add(net.network_id);
    setBusyNet(net.network_id);
    setErr(null);
    try {
      await api.setPublishNetworks(entry.id, [...selected]);
      await panel.reload();
      onChanged();
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusyNet(null);
    }
  }

  return (
    <Drawer
      title={<>Publish · {entry.name}</>}
      subtitle={<span className="muted small">Choose the networks this service is available in</span>}
      onClose={onClose}
    >
      {autoOpened && !unsupported && (
        <div className="publish-callout">
          <b>Service activated.</b> It is available wherever you publish it. Pick the networks below — changes apply immediately.
        </div>
      )}
      {err && <ErrorState message={err} />}
      {panel.loading ? (
        <Loading />
      ) : unsupported ? (
        <p className="muted">This gateway does not support per-network publishing.</p>
      ) : panel.error ? (
        <ErrorState message={panel.error} onRetry={panel.reload} />
      ) : nets.length === 0 ? (
        <p className="muted">
          You have no networks you can publish into. Create a network (where you are the owner) first, then
          publish this service into it.
        </p>
      ) : (
        <>
          <p className="muted small">
            Published in {publishedCount} of {nets.length} network{nets.length === 1 ? '' : 's'}.
          </p>
          <ul className="publish-net-list">
            {nets.map((n) => (
              <li key={n.network_id} className="publish-net-row">
                <label className="check publish-net-check">
                  <input
                    type="checkbox"
                    checked={n.published}
                    disabled={!n.can_publish || busyNet === n.network_id}
                    onChange={() => toggle(n)}
                  />
                  <span className="publish-net-name">
                    <b>{n.name}</b> <code className="mono small">{n.slug}</code>
                  </span>
                </label>
                {busyNet === n.network_id ? (
                  <span className="muted small">applying…</span>
                ) : n.published ? (
                  <Badge tone="ok">published</Badge>
                ) : (
                  <span className="muted small">not published</span>
                )}
              </li>
            ))}
          </ul>
          <p className="muted small">
            A published service&apos;s tools become available in that network as soon as a node serving it
            connects. Unpublishing withdraws them immediately.
          </p>
        </>
      )}
    </Drawer>
  );
}

// The Active column toggle. For a PERSONAL def it is fixed (always active, yours).
// For a SHARED def: admin → global PATCH active; user → POST per-user activation.
function ActiveToggle({
  entry,
  isAdmin,
  onChanged,
  onActivated,
}: {
  entry: ServiceCatalogEntry;
  isAdmin: boolean;
  onChanged: () => void;
  // H15: called when the service is turned ON, so the parent can auto-open the network slide-out.
  onActivated?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (entry.kind === 'personal') {
    return <Badge tone="ok">yours</Badge>;
  }

  const checked = isAdmin ? entry.active : entry.active_for_me;

  async function toggle(ev: React.ChangeEvent<HTMLInputElement>) {
    ev.stopPropagation();
    const turnedOn = isAdmin ? !entry.active : !entry.active_for_me;
    setBusy(true);
    setErr(null);
    try {
      if (isAdmin) await api.patchServiceActive(entry.id, !entry.active);
      else await api.setServiceActivation(entry.id, !entry.active_for_me);
      onChanged();
      // Auto-open the network slide-out right after activation (H15 acceptance #2).
      if (turnedOn) onActivated?.();
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="switch" onClick={(e) => e.stopPropagation()} title={err ?? undefined}>
      <input type="checkbox" checked={checked} disabled={busy} onChange={toggle} />
      <span className="switch-track" />
    </label>
  );
}

function ServiceEntryDrawer({
  entry,
  canManage,
  isAdmin,
  onClose,
  onChanged,
  onManageNetworks,
}: {
  entry: ServiceCatalogEntry;
  canManage: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onChanged: (next: ServiceCatalogEntry | null) => void;
  onManageNetworks: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [openTool, setOpenTool] = useState<string | null>(null);

  async function del() {
    setBusy(true);
    setErr(null);
    try {
      await api.deleteServiceDefinition(entry.id);
      onChanged(null);
      onClose();
    } catch (e) {
      setErr(friendly(e));
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={entry.name}
      subtitle={<code className="mono small">{entry.image_ref}</code>}
      onClose={onClose}
      actions={
        <>
          <button className="btn btn-sm btn-primary" onClick={() => setTesting(true)}>Test</button>
          {entry.active_for_me && (
            <button className="btn btn-sm" onClick={onManageNetworks}>Networks</button>
          )}
          {canManage ? (
            <>
              <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>
              <button className="btn btn-sm btn-danger" onClick={() => setConfirmDel(true)}>Delete</button>
            </>
          ) : (
            <span className="muted small">read-only</span>
          )}
        </>
      }
    >
      {err && <ErrorState message={err} />}
      {entry.description && <p className="svc-drawer-desc">{entry.description}</p>}
      <DetailRow label="Catalog">
        {entry.kind === 'shared' ? <Badge tone="info">shared</Badge> : <Badge tone="muted">personal</Badge>}
      </DetailRow>
      <DetailRow label="Source">
        <Badge tone={sourceTone(entry.source_kind)}>{entry.source}</Badge>
      </DetailRow>
      {entry.tags.length > 0 && (
        <DetailRow label="Tags">
          <div className="svc-tags">
            {entry.tags.map((t) => <span key={t} className="chip chip-tag">{t}</span>)}
          </div>
        </DetailRow>
      )}
      {entry.kind === 'shared' && (
        <DetailRow label="Globally active">
          {entry.active ? <Badge tone="ok">active</Badge> : <Badge tone="bad">inactive</Badge>}
        </DetailRow>
      )}
      <DetailRow label="Enabled for me">
        {entry.active_for_me ? <Badge tone="ok">yes</Badge> : <Badge tone="muted">no</Badge>}
      </DetailRow>
      <DetailRow label="Spec hash"><code className="mono small">{entry.spec_hash}</code></DetailRow>
      {entry.gpu_class?.min_vram_gb != null && <DetailRow label="Min VRAM">{entry.gpu_class.min_vram_gb} GB</DetailRow>}
      <DetailRow label="Owner"><code className="mono small">{entry.owner_id}</code></DetailRow>

      <div>
        <h4>Tools</h4>
        <ul className="tool-list">
          {entry.tools.map((t) => (
            <ToolSummaryRow key={t.id} tool={t} open={openTool === t.id} onToggle={() => setOpenTool(openTool === t.id ? null : t.id)} />
          ))}
          {entry.tools.length === 0 && <li className="muted small">No tools registered yet.</li>}
        </ul>
      </div>

      {testing && <TestPanelDrawer entry={entry} onClose={() => setTesting(false)} />}
      {editing && (
        <ServiceDefinitionModal
          entry={entry}
          isAdmin={isAdmin}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged(entry);
          }}
        />
      )}
      {confirmDel && (
        <ConfirmDialog
          title="Delete service"
          message={<>Delete <b>{entry.name}</b>? Any publications referencing it must be withdrawn first.</>}
          confirmLabel="Delete"
          busy={busy}
          error={err}
          onConfirm={del}
          onClose={() => setConfirmDel(false)}
        />
      )}
    </Drawer>
  );
}

// Create/edit a service-definition shell. On create, the admin may choose the
// catalog kind (shared|personal); a non-admin always creates a PERSONAL def. The
// gateway requires a spec_hash for a shell, so the dashboard supplies a
// deterministic placeholder. Editing changes name/image/GPU only (kind is fixed).
function ServiceDefinitionModal({
  entry,
  isAdmin,
  onClose,
  onSaved,
}: {
  entry?: ServiceCatalogEntry;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = !!entry;
  const [name, setName] = useState(entry?.name ?? '');
  const [imageRef, setImageRef] = useState(entry?.image_ref ?? '');
  const [minVram, setMinVram] = useState(entry?.gpu_class?.min_vram_gb != null ? String(entry.gpu_class.min_vram_gb) : '');
  const [kind, setKind] = useState<ServiceKind>(entry?.kind ?? 'personal');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const gpu_class = minVram.trim() ? { min_vram_gb: Number(minVram) } : undefined;
    try {
      if (editing) {
        await api.patchServiceDefinition(entry!.id, { name: name.trim(), image_ref: imageRef.trim(), gpu_class });
      } else {
        const spec_hash = `sha256:ui-${Date.now().toString(16)}`;
        await api.createServiceDefinition(
          { name: name.trim(), image_ref: imageRef.trim(), spec_hash, gpu_class },
          isAdmin ? kind : 'personal',
        );
      }
      onSaved();
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={editing ? 'Edit service' : isAdmin ? 'New service' : 'Add personal service'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !name.trim() || !imageRef.trim()} onClick={submit}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create'}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="issue-form">
        {!editing && isAdmin && (
          <label className="field">Catalog kind
            <select value={kind} onChange={(e) => setKind(e.target.value as ServiceKind)}>
              <option value="personal">personal (only you)</option>
              <option value="shared">shared template (whole org)</option>
            </select>
          </label>
        )}
        <label className="field">Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="whisper-large-v3" required />
        </label>
        <label className="field">Image ref
          <input value={imageRef} onChange={(e) => setImageRef(e.target.value)} placeholder="registry.example.com/whisper:1.2" required />
        </label>
        <label className="field">Min VRAM (GB, optional)
          <input type="number" min="0" value={minVram} onChange={(e) => setMinVram(e.target.value)} placeholder="10" />
        </label>
        <p className="small muted">Tools &amp; schemas are registered by the providing client after the definition exists.</p>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}

// H11: search Docker Hub and import a chosen image as a ServiceDefinition. The Docker Hub source plugs
// into the H13 CatalogSource abstraction server-side; here we drive its search/import API. A gateway
// predating H11 404s (shown inline); a gateway host without internet 503s (shown inline) — either way
// the created service lands in the catalog (tagged `dockerhub`) and is immediately activable.
function DockerHubImportModal({
  isAdmin,
  onClose,
  onImported,
}: {
  isAdmin: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DockerHubSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The image being imported (its repo_name) → busy state; and per-row chosen tag.
  const [busyRepo, setBusyRepo] = useState<string | null>(null);
  const [tags, setTags] = useState<Record<string, string>>({});
  const [kind, setKind] = useState<ServiceKind>(isAdmin ? 'shared' : 'personal');
  const [importedRepos, setImportedRepos] = useState<Set<string>>(new Set());

  async function runSearch(e?: React.FormEvent) {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setErr(null);
    setSearched(true);
    try {
      const resp = await api.dockerhubSearch(query.trim(), 25);
      setResults(resp.results);
    } catch (ex) {
      setErr(friendly(ex));
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function importRepo(r: DockerHubSearchResult) {
    setBusyRepo(r.repo_name);
    setErr(null);
    try {
      await api.dockerhubImport(
        {
          repo_name: r.repo_name,
          tag: (tags[r.repo_name] || 'latest').trim() || 'latest',
          description: r.description || undefined,
          is_official: r.is_official,
        },
        isAdmin ? kind : 'personal',
      );
      setImportedRepos((s) => new Set(s).add(r.repo_name));
      onImported();
    } catch (ex) {
      setErr(friendly(ex));
    } finally {
      setBusyRepo(null);
    }
  }

  return (
    <Modal
      title="Import from Docker Hub"
      onClose={onClose}
      footer={<button className="btn btn-sm" onClick={onClose}>Close</button>}
    >
      <form className="catalog-toolbar dockerhub-search" onSubmit={runSearch}>
        <input
          className="catalog-search"
          type="search"
          autoFocus
          placeholder="Search Docker Hub (e.g. nginx, mcp, whisper)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {isAdmin && (
          <select value={kind} onChange={(e) => setKind(e.target.value as ServiceKind)}>
            <option value="shared">shared (whole org)</option>
            <option value="personal">personal (only me)</option>
          </select>
        )}
        <button className="btn btn-sm btn-primary" disabled={searching || !query.trim()}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {err && <div className="form-error">{err}</div>}

      {searching ? (
        <Loading />
      ) : searched && results.length === 0 && !err ? (
        <p className="muted">No Docker Hub repositories matched "{query}".</p>
      ) : (
        <ul className="dockerhub-results">
          {results.map((r) => {
            const done = importedRepos.has(r.repo_name);
            return (
              <li key={r.repo_name} className="dockerhub-result">
                <div className="dockerhub-result-main">
                  <div className="dockerhub-result-head">
                    <code className="mono">{r.repo_name}</code>
                    {r.is_official && <Badge tone="ok">official</Badge>}
                    <span className="muted small">★ {r.stars.toLocaleString()}</span>
                  </div>
                  {r.description && <div className="muted small svc-desc">{r.description}</div>}
                </div>
                <div className="dockerhub-result-actions">
                  <input
                    className="dockerhub-tag"
                    placeholder="tag (latest)"
                    value={tags[r.repo_name] ?? ''}
                    onChange={(e) => setTags((m) => ({ ...m, [r.repo_name]: e.target.value }))}
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={busyRepo === r.repo_name || done}
                    onClick={() => importRepo(r)}
                  >
                    {busyRepo === r.repo_name ? 'Importing…' : done ? '✓ Imported' : 'Import'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="muted small">
        Imports create a service definition (image prefilled, default port 8000, env placeholders) tagged
        <code> dockerhub</code>. Tools appear once a node runs the container. Docker Hub search runs from
        the gateway host — if it has no internet access, search returns an error.
      </p>
    </Modal>
  );
}

// H13: admin-only management of catalog sources (built-in config/db + custom
// remote URLs). A gateway predating this endpoint 404s → the card hides itself.
function CatalogSourcesCard({ onChanged }: { onChanged: () => void }) {
  const sources = useResource(() => api.catalogSources(), []);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Unsupported on an older gateway — render nothing rather than an error box.
  if (sources.error && /404|not found/i.test(sources.error)) return null;

  async function toggle(s: CatalogSource) {
    setErr(null);
    try {
      await api.patchCatalogSource(s.id, { enabled: !s.enabled });
      sources.reload();
      onChanged();
    } catch (e) {
      setErr(friendly(e));
    }
  }
  async function remove(s: CatalogSource) {
    setErr(null);
    try {
      await api.deleteCatalogSource(s.id);
      sources.reload();
      onChanged();
    } catch (e) {
      setErr(friendly(e));
    }
  }
  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createCatalogSource({ name: name.trim(), url: url.trim() });
      setName('');
      setUrl('');
      setAdding(false);
      sources.reload();
      onChanged();
    } catch (ex) {
      setErr(friendly(ex));
    } finally {
      setBusy(false);
    }
  }

  // H40 — the toggle (Enabled) is the FIRST column; every column header is click-to-sort.
  const cols: Column<CatalogSource>[] = [
    {
      key: 'enabled',
      header: 'Enabled',
      align: 'center',
      sortValue: (s) => s.enabled,
      cell: (s) => (
        <label className="switch" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={s.enabled} onChange={() => toggle(s)} />
          <span className="switch-track" />
        </label>
      ),
    },
    { key: 'name', header: 'Source', sortValue: (s) => s.name, cell: (s) => <b>{s.name}</b> },
    { key: 'kind', header: 'Kind', sortValue: (s) => s.kind, cell: (s) => <Badge tone={sourceTone(s.kind)}>{s.kind}</Badge> },
    { key: 'url', header: 'URL', sortValue: (s) => s.url ?? '', cell: (s) => (s.url ? <code className="mono small">{s.url}</code> : '—') },
    { key: 'prio', header: 'Priority', align: 'right', sortValue: (s) => s.priority, cell: (s) => s.priority },
    {
      key: 'status',
      header: 'Status',
      sortValue: (s) => (s.ok === false ? -1 : s.count ?? null),
      cell: (s) =>
        s.ok === false ? (
          <span title={s.error ?? undefined}><Badge tone="bad">error</Badge></span>
        ) : s.count != null ? (
          <span className="muted small">{s.count} entries</span>
        ) : (
          '—'
        ),
    },
    {
      key: 'act',
      header: '',
      align: 'right',
      cell: (s) =>
        s.builtin ? (
          <span className="muted small">built-in</span>
        ) : (
          <button className="btn btn-sm btn-danger" onClick={() => remove(s)}>Remove</button>
        ),
    },
  ];

  return (
    <Card
      title="Catalog sources"
      actions={<button className="btn btn-sm" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : '+ Remote source'}</button>}
    >
      {err && <ErrorState message={err} />}
      {adding && (
        <form className="issue-form catalog-source-form" onSubmit={add}>
          <label className="field">Name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Partner registry" required />
          </label>
          <label className="field">List URL
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/services.json" required />
          </label>
          <button className="btn btn-sm btn-primary" disabled={busy || !name.trim() || !url.trim()}>
            {busy ? 'Adding…' : 'Add source'}
          </button>
        </form>
      )}
      {sources.loading ? (
        <Loading />
      ) : sources.error || !sources.data ? (
        <ErrorState message={sources.error ?? 'no data'} onRetry={sources.reload} />
      ) : (
        <>
          <DataTable columns={cols} rows={sources.data.items} rowKey={(s) => s.id} empty="No sources." />
          <p className="muted small">
            The catalog merges all enabled sources (higher priority wins on id collision). A remote source
            returns a JSON list of services; a down source is skipped and never breaks the catalog.
          </p>
        </>
      )}
    </Card>
  );
}

function ToolSummaryRow({ tool, open, onToggle }: { tool: ServiceToolSummary; open: boolean; onToggle: () => void }) {
  return (
    <li className="tool-row">
      <div className="tool-row-head" onClick={onToggle}>
        <code className="mono">{tool.name}</code>
        <div className="tool-flags">
          {tool.streaming && <Badge tone="info">streaming</Badge>}
          {tool.idempotent && <Badge tone="muted">idempotent</Badge>}
          {tool.media_type && <span className="chip">{tool.media_type}</span>}
        </div>
        <span className="caret">{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div className="tool-schemas">
          <div>
            <h4>input_schema</h4>
            <JsonView value={tool.input_schema ?? {}} />
          </div>
          {tool.output_schema != null && (
            <div>
              <h4>output_schema</h4>
              <JsonView value={tool.output_schema} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}
